import { useMemo } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import {
  FloorTypename,
  ProjectTypename,
  RoomTypename
} from "shared/types/Project"

import { useDefaultProjectChildPosition } from "entities/projects/model/useDefaultProjectChildPosition"
import { useRoomMutations } from "entities/rooms/model/useRoomMutations"
import { RoomFormData } from "features/room-management/types/RoomFormData"
import { useRoomForm } from "widgets/room/room-form/model/useRoomForm"

import { NewRoomOverlayModel } from "../NewRoomOverlay"
import { NewRoomOverlayPayload } from "../NewRoomOverlayConnector"

const defaultNewRoom = { position: 0 }

interface NewRoomOverlayModelArgs {
  payload: NewRoomOverlayPayload
}

export function useNewRoomOverlayModel({
  payload: { room, projectID, hideProject }
}: NewRoomOverlayModelArgs): NewRoomOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { createRoom } = useRoomMutations()

  const { defaultParentID, defaultPosition } = useDefaultProjectChildPosition({
    projectID,
    childType: RoomTypename
  })

  const defaultRoom = useMemo(() => ({ ...room, ...defaultNewRoom }), [room])

  const submitRoom = useEvent((roomFormData: RoomFormData) =>
    createRoom({
      room: roomFormData.room,
      displayPosition: roomFormData.displayPosition,
      parentType:
        roomFormData.parentID === roomFormData.projectID
          ? ProjectTypename
          : FloorTypename,
      projectID: roomFormData.projectID || projectID,
      parentID: roomFormData.parentID || defaultParentID
    })
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<RoomFormData>({
    onSubmit: submitRoom,
    onSuccess: closeOverlay
  })

  const roomFormModel = useRoomForm({
    room: defaultRoom,
    projectID,
    formOptions: {
      hideProject,
      showHint: true,
      hideClarificationRequest: true,
      hideAttachments: true
    },
    parentID: defaultParentID,
    displayPosition: defaultPosition,
    onSubmit: handleSubmit
  })

  return {
    roomFormModel,
    isLoading: isSubmitting,
    isSubmitting,
    onCancel: closeOverlay
  }
}
